/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Checkbox, InlineNotification, Typography2 } from '@coursera/cds-core';

import ModalFooter from 'bundles/enterprise-admin-users/components/ModalFooter';
import MutationResultDetails, { RESULT_TYPES } from 'bundles/enterprise-admin-users/components/MutationResultDetails';
import { SimpleContractTable, SimpleProgramTable } from 'bundles/enterprise-admin-users/components/SimpleTables';
import { ASYNCHRONOUS_IMPORT, SYNCHRONOUS_IMPORT } from 'bundles/enterprise-admin-users/constants';
import type CSVInvitationModel from 'bundles/enterprise-admin-users/models/CSVInvitation';
import type { ImportProcessingTypes, Program, UserRoster } from 'bundles/enterprise-admin-users/types';
import { DataDelayNotification, Spacer } from 'bundles/enterprise-ui';

import _t from 'i18n!nls/enterprise-admin-users';

import 'css!./__styles__/ImportLearnersPreview';

type Props = {
  selectedProgram?: Program;
  selectedRoster?: UserRoster;
  processingType?: ImportProcessingTypes;
  csvInvitation?: CSVInvitationModel;
  sendInvitations: boolean;
  error?: string;
  handleClose: () => void;
  onPreviousStep: () => void;
  onContinue: () => void;
  toggleSendInvitations: () => void;
};

class ImportLearnersPreview extends React.Component<Props> {
  handleCheckboxChange = () => {
    this.props.toggleSendInvitations();
  };

  getFormattedResults = () => {
    const { csvInvitation, processingType, sendInvitations } = this.props;
    return processingType === ASYNCHRONOUS_IMPORT
      ? [
          {
            id: 'warning',
            type: RESULT_TYPES.WARNING,
            label: _t('Unable to parse'),
            count: csvInvitation?.totalSkippedCount,
          },
          {
            id: 'warning',
            type: RESULT_TYPES.ERROR,
            label: _t('Invalid emails'),
            count: csvInvitation?._inviteesWithInvalidEmail.length,
            details: csvInvitation?._inviteesWithInvalidEmail.join(', '),
          },
          {
            id: 'warning',
            type: RESULT_TYPES.ERROR,
            label: _t('Invalid external IDs'),
            count: csvInvitation?._invalidExternalIds.length,
            details: csvInvitation?._invalidExternalIds.join(', '),
          },
          {
            id: 'warning',
            type: RESULT_TYPES.ERROR,
            label: _t('Invalid name'),
            count: csvInvitation?._inviteesWithInvalidNames.length,
            details: csvInvitation?._inviteesWithInvalidNames.join(', '),
          },
        ].filter((_) => _.count)
      : [
          {
            id: 'info',
            type: RESULT_TYPES.INFO,
            label: sendInvitations ? _t('Invitations to be sent') : _t('Learners to import'),
            count: csvInvitation?.invitationCount,
          },
          {
            id: 'warning',
            type: RESULT_TYPES.WARNING,
            label: _t('Existing members'),
            count: csvInvitation?._skippedMemberCount,
          },
          {
            id: 'warning',
            type: RESULT_TYPES.ERROR,
            label: _t('Invalid emails'),
            count: csvInvitation?._inviteesWithInvalidEmail.length,
            details: csvInvitation?._inviteesWithInvalidEmail.join(', '),
          },
          {
            id: 'warning',
            type: RESULT_TYPES.ERROR,
            label: _t('Invalid external ID'),
            count: csvInvitation?._invalidExternalIds.length,
            details: csvInvitation?._invalidExternalIds.join(', '),
          },
          {
            id: 'warning',
            type: RESULT_TYPES.ERROR,
            label: _t('Invalid name'),
            count: csvInvitation?._inviteesWithInvalidNames.length,
            details: csvInvitation?._inviteesWithInvalidNames.join(', '),
          },
          {
            id: 'warning',
            type: RESULT_TYPES.WARNING,
            label: _t('Existing invitations'),
            count: csvInvitation?._existingInvitationCount,
          },
        ].filter((_) => _.count);
  };

  getHeaderText = () => {
    const { sendInvitations } = this.props;
    return sendInvitations ? _t('Import and send invitations') : _t('Add Learners');
  };

  getSubHeaderText = () => {
    const { sendInvitations } = this.props;
    return sendInvitations ? _t('Invitations') : _t('Learners');
  };

  render() {
    const { error, handleClose, onPreviousStep, selectedRoster, selectedProgram, onContinue, csvInvitation } =
      this.props;
    const { sendInvitations, processingType } = this.props;
    const externalIdsAreInvalid = Boolean(csvInvitation?._inviteesWithInvalidExternalId.length);
    const emailsAreInvalid = Boolean(csvInvitation?._inviteesWithInvalidEmail.length);
    const namesAreInvalid = Boolean(csvInvitation?._inviteesWithInvalidNames.length);
    return (
      <div
        className="rc-ImportLearnersPreview"
        data-e2e={sendInvitations ? 'sendInvitations' : 'sendInvitationsDisabled'}
      >
        <Typography2
          component="h2"
          variant="titleSmall"
          css={css`
            margin-bottom: var(--cds-spacing-200);
          `}
        >
          {this.getHeaderText()}
        </Typography2>
        {processingType === SYNCHRONOUS_IMPORT && (
          <React.Fragment>
            <DataDelayNotification />
            <Spacer size={16} />
          </React.Fragment>
        )}
        {processingType === ASYNCHRONOUS_IMPORT && (
          <React.Fragment>
            <InlineNotification severity="information">
              {externalIdsAreInvalid || emailsAreInvalid
                ? _t(
                    "After verifying a few rows from your uploaded CSV file, we've identified some formatting errors. Please review the details below and make the necessary corrections before re-uploading the CSV."
                  )
                : _t(
                    'Looks like you uploaded a large file, so we were only able to preview a few rows. After you confirm the upload, we will begin processing the full file and notify you via email once it’s complete.'
                  )}
            </InlineNotification>
            <Spacer size={16} />
          </React.Fragment>
        )}
        {externalIdsAreInvalid && (
          <InlineNotification
            severity="warning"
            css={css`
              margin-bottom: var(--cds-spacing-200);
            `}
          >
            {_t('External ID must be shorter than 250 characters.')}
          </InlineNotification>
        )}
        {emailsAreInvalid && (
          <InlineNotification
            severity="warning"
            css={css`
              margin-bottom: var(--cds-spacing-200);
            `}
          >
            {_t('Valid emails must contain a user name, the @ symbol, and a domain name.')}
          </InlineNotification>
        )}
        {namesAreInvalid && (
          <InlineNotification
            severity="warning"
            css={css`
              margin-bottom: var(--cds-spacing-200);
            `}
          >
            {_t('Valid names must contain only utf-8 characters.')}
          </InlineNotification>
        )}
        <Typography2
          component="h3"
          variant="subtitleMedium"
          css={css`
            margin-bottom: var(--cds-spacing-200);
          `}
        >
          {this.getSubHeaderText()}
        </Typography2>
        {processingType === ASYNCHRONOUS_IMPORT && !emailsAreInvalid && !externalIdsAreInvalid ? (
          <div>{_t('Learner list is too large to display.')}</div>
        ) : (
          <MutationResultDetails results={this.getFormattedResults()} />
        )}
        <div className="m-t-2 m-b-2">
          <Checkbox
            onChange={this.handleCheckboxChange}
            checked={sendInvitations}
            data-testid="send-bulk-emails-checkbox"
            label={_t('Send email invitations to new members after import')}
          />
        </div>
        <Typography2 component="h3" variant="subtitleMedium">
          {_t('Program')}
        </Typography2>
        <SimpleProgramTable programs={selectedProgram ? [selectedProgram] : []} />
        {selectedRoster && (
          <span>
            <Typography2
              component="h3"
              variant="subtitleMedium"
              css={css`
                margin-top: var(--cds-spacing-200);
              `}
            >
              {_t('Contract')}
            </Typography2>
            <SimpleContractTable userRosters={[selectedRoster]} />
          </span>
        )}
        <div className="m-t-1 m-b-1 vertical-box">
          {error && (
            <InlineNotification severity="error" style={{ marginTop: 10 }}>
              {error}
            </InlineNotification>
          )}
        </div>
        <ModalFooter
          onContinue={onContinue}
          onContinueLabel={sendInvitations ? _t('Send invitations') : _t('Add Learners')}
          disabled={!!error}
          onCancel={handleClose}
          onPrevious={onPreviousStep}
        />
      </div>
    );
  }
}

export default ImportLearnersPreview;
